import React, { forwardRef, useCallback, useEffect, useState, useImperativeHandle } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogOverlay } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

import { X } from "lucide-react";


const ALLOWED = ["jpg", "jpeg", "png", "webp", "svg"];

async function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

async function getCroppedImage(imageSrc: string, cropPixels: { width: number; height: number; x: number; y: number }) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropPixels.width));
  canvas.height = Math.max(1, Math.round(cropPixels.height));
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Canvas 2D context not available");

  ctx.drawImage(
    image,
    Math.round(cropPixels.x),
    Math.round(cropPixels.y),
    Math.round(cropPixels.width),
    Math.round(cropPixels.height),
    0,
    0,
    Math.round(cropPixels.width),
    Math.round(cropPixels.height)
  );

  return canvas.toDataURL("image/jpeg", 0.92);
}

export type ImageCropperRef = {
  openWithFile: (file: File) => void;
};

const ImageCropperEasy = forwardRef<ImageCropperRef,{
  aspectRatio?: number;
  initialImage?: string | null;
  initialFileName?: string;
  onSave: (data: { croppedUrl: string; fileName: string }) => void;
  isPreviewVisible?: boolean;
}>(({ aspectRatio = 1, initialImage, initialFileName = "", onSave, isPreviewVisible = true }, ref)=> {

  const [modalOpen, setModalOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, msg: "" });

  // stored displayed/cropped image
  const [workingImage, setWorkingImage] = useState<string | null>(null);
  const [fileName, setFileName] = useState(initialFileName);

  // cropper state
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  useEffect(() => {
    if (initialImage) {
      setFileName(initialFileName);
    }
  }, [initialImage, initialFileName]);

  // fixed preview size (square)
  const PREVIEW_SIZE = 200;

  const isMobile = window.innerWidth < 768;
  const showPreview = isPreviewVisible && !isMobile;

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixelsArg: any) => {
    setCroppedAreaPixels(croppedAreaPixelsArg);
  }, []);

  const handleFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED.includes(ext)) {
      setAlert({ open: true, msg: "Only JPG, PNG, WEBP, SVG allowed." });
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setWorkingImage(reader.result as string);
      setModalOpen(true);
      // reset cropper values
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);
  };

  useImperativeHandle(ref, () => ({
    openWithFile(file: File) {
      handleFile(file);
    },
  }));

  const saveCropped = useCallback(async () => {
    try {
      if (!workingImage || !croppedAreaPixels) {
        setAlert({ open: true, msg: "Please select and crop an image first." });
        return;
      }
      // react-easy-crop gives croppedAreaPixels relative to the source (natural) image
      const croppedDataUrl = await getCroppedImage(workingImage, croppedAreaPixels);
      onSave({ croppedUrl: croppedDataUrl, fileName });
      setModalOpen(false);
    } catch (err: any) {
      console.error(err);
      setAlert({ open: true, msg: "Failed to crop image." });
    }
  }, [workingImage, croppedAreaPixels, fileName, onSave]);

  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const updatePreview = useCallback(async () => {
    if (!workingImage || !croppedAreaPixels) {
      setPreviewDataUrl(null);
      return;
    }
    try {
      const dataUrl = await getCroppedImage(workingImage, croppedAreaPixels);
      setPreviewDataUrl(dataUrl);
    } catch {
      setPreviewDataUrl(null);
    }
  }, [workingImage, croppedAreaPixels]);

  // update preview whenever crop changes
  React.useEffect(() => {
    updatePreview();
  }, [croppedAreaPixels, workingImage, updatePreview]);

  return (
    <>
      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogOverlay className="fixed inset-0 bg-black/70 z-50" />
        <DialogContent className="fixed inset-0 w-full bg-[#18181B] translate-x-0 translate-y-0 text-white z-50 p-0 md:inset-auto md:left-1/2 md:top-1/2 md:max-w-5xl md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg border border-[#2F2F32] overflow-hidden">
            <div className="h-14 px-4 flex items-center justify-between border-b border-[#2F2F32]">
              <h2 className="text-sm font-semibold">Edit Photo</h2>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(false);
                }}
                className="h-8 w-8 rounded-full bg-[#2F2F32] flex items-center justify-center"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
          <div className="flex flex-col md:flex-row h-full">
            {/* Crop area (left) */}
            <div className="w-full md:w-[70%] bg-black h-[calc(100vh-56px)] md:h-[520px]">
              {workingImage && (
                <div className="relative w-full h-[calc(100%-78px)]">
                  <Cropper
                    image={workingImage}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspectRatio}
                    onCropChange={(c) => setCrop(c)}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    showGrid={true}
                    cropShape="rect"
                    objectFit="contain"
                    style={{ containerStyle: { width: "100%", height: "100%" } } as any}
                  />
                </div>
              )}
              <div className="mt-auto w-full flex gap-3 px-4">
                <Button variant="outline" className="w-1/2 bg-[#2F2F32] text-white border-none" onClick={(e) => { e.stopPropagation(); setModalOpen(false); }}>Cancel</Button>
                <Button className="w-1/2 bg-[#EF4444] hover:bg-[#dc2626]" onClick={(e) => { e.stopPropagation(); saveCropped();}}>Save</Button>
              </div>
            </div>

            {/* Controls + Preview (right) */}
            {showPreview && (
              <div className="w-full md:w-[30%] p-4 bg-[#212124] flex flex-col items-center">
                <p className="font-semibold mb-3 text-white">Preview</p>

                <div
                  className="relative border border-[#2F2F32] rounded-md overflow-hidden bg-[#18181B]"
                  style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
                >
                  {previewDataUrl ? (
                    <img src={previewDataUrl} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm text-[#AFAFAF]">Preview</div>
                  )}
                </div>

                <div className="w-full mt-5">
                  <label className="text-sm text-[#AFAFAF] mb-2 block">Zoom</label>
                  <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full"/>
                  <div className="text-xs text-[#AFAFAF] mt-1">{zoom.toFixed(2)}x</div>
                </div>

              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert */}
      <AlertDialog open={alert.open} onOpenChange={(o) => setAlert({ ...alert, open: o })}>
      <AlertDialogContent
        className="
          pointer-events-auto
          fixed
          top-1/2
          left-1/2
          w-[90%]
          -translate-x-1/2
          -translate-y-1/2
          rounded-[16px]
          bg-[#181b1b]
        "
      >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Error</AlertDialogTitle>
            <AlertDialogDescription className="text-[#F3F3F3]">{alert.msg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

export default ImageCropperEasy;
